import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { Button, Confirm, Message } from 'semantic-ui-react';
import { get, toNumber } from 'lodash';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import AuctionExceptionList from './List';
import AuctionExceptionForm from './AuctionException/Form';
import client from '../../apollo';
import apmsLocationListQuery from '../../graphql/apmsLocationListQuery';
import { setPage } from '../../data/page/actions';
import withPagination from '../../containers/WithPagination';
import autoCancellationExceptionListQuery from '../../graphql/autoCancellationExceptionListQuery';
import createAutoCancellationExceptionMutation from '../../graphql/createAutoCancellationExceptionMutation';
import updateAutoCancellationExceptionMutation from '../../graphql/updateAutoCancellationExceptionMutation';
import deleteAutoCancellationExceptionMutation from '../../graphql/deleteAutoCancellationExceptionMutation';

const defaultAuctionException = {
  id: 0,
  auctionLocation: '',
  gracePeriod: '',
  exceptionReason: '',
  exceptionEndDate: '',
  createdBy: '',
  createdAt: '',
  lastUpdatedBy: '',
  lastUpdatedAt: '',
};
const fieldsToCompare = ['gracePeriod', 'exceptionEndTime', 'exceptionReason'];

function isChange(values, selectedAuctionException, fields) {
  return fields.some(
    field => values[field] !== selectedAuctionException[field]
  );
}

function getCreateExpiredVraGracePeriodMutationInput(action, values) {
  return {
    exceptionType: 'AUCTION',
    exceptionSourceId: toNumber(values.auctionId),
    gracePeriod: toNumber(values.gracePeriod),
    exceptionEndTime: values.exceptionEndTime,
    exceptionReason: values.exceptionReason,
    id: values.id ? toNumber(values.id) : 0,
  };
}

class AuctionExceptions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedAuctionException: null,
      isDeleteConfirmationOpen: false,
      isModalOpen: false,
      formError: null,
      formSubmitting: null,
      flashMessage: null,
      isNegativeFlashMessage: false,
      locations: [],
    };
  }

  handleDeleteCancel = () => {
    this.setState({ isDeleteConfirmationOpen: false });
  };

  onSelectAuctionException = (action, auctionException) => {
    this.setState({
      selectedAuctionException: auctionException,
      flashMessage: null,
      isNegativeFlashMessage: false,
    });

    switch (action) {
      case 'Edit':
        this.setState({
          isModalOpen: true,
        });
        break;
      case 'Delete':
        this.setState({
          isDeleteConfirmationOpen: true,
        });
        break;
      default:
        this.setState({
          selectedAuctionException: null,
        });
    }
  };

  onFormCloseHandler = () => {
    this.setState({
      isModalOpen: false,
      selectedAuctionException: null,
      formError: null,
      formSubmitting: null,
    });
  };

  onClickNewAuction = () => {
    this.setState({
      selectedAuctionException: defaultAuctionException,
      isModalOpen: true,
    });
  };

  onFormSubmitHandler = async values => {
    const {
      createMutation,
      updateMutation,
    } = this.props;
    const { selectedAuctionException } = this.state;
    this.setState({
      formSubmitting: true,
    });
    const { action } = values;
    try {
      switch (action) {
        case 'Create':
          await this.onExcuteMutationAuctionException(
            createMutation,
            'createAutoCancellationException',
            values,
            action
          );
          break;
        case 'Update':
          if (
            isChange(values, selectedAuctionException, fieldsToCompare)
          ) {
            await this.onExcuteMutationAuctionException(
              updateMutation,
              'updateAutoCancellationException',
              values,
              action
            );
          } else {
            // unchanged
            this.onFormCloseHandler();
          }
          break;
        default:
          break;
      }
    } catch (error) {
      this.onFailedSubmitAuctionException(
        action,
        Array.isArray(error) ? error : error.message
      );
    }
  };

  handleDeleteConfirm = async() => {
    const {
      selectedAuctionException: { id },
    } = this.state;
    const { deleteMutation } = this.props;
    const action = 'Delete';
    try {
      await this.onExcuteMutationAuctionException(
        deleteMutation,
        'deleteAutoCancellationException',
        { id },
        action
      );
    } catch (error) {
      this.onFailedSubmitAuctionException(
        action,
        Array.isArray(error) ? error : error.message
      );
    }
  };

  locationGetAllQuery = () => {
    client
      .query({
        query: apmsLocationListQuery,
        variables: {
          page: 1,
          size: 200,
          active: true,
        },
      })
      .then(({ data }) => {
        this.setState({
          locations: get(data, 'apmsLocationList'),
        });
      })
      .catch(() => {
        this.setState({ locations: [] });
      });
  };

  successSubmitAuctionException = action => {
    this.setState({
      formSubmitting: false,
      formError: null,
      flashMessage: `Successfully ${action}d Auction Exception.`,
    });
  };

  onFailedSubmitAuctionException = (action, errorMessage) => {
    if (action === 'Delete') {
      this.setState({
        isDeleteConfirmationOpen: false,
        flashMessage: `Unsuccessfully Deleted Auction Exception with error: ${errorMessage}`,
        isNegativeFlashMessage: true,
      });
    } else {
      this.setState({
        formSubmitting: false,
        formError: errorMessage,
      });
    }
  };

  onExcuteMutationAuctionException = async(
    mutation,
    mutationResultName,
    values,
    action
  ) => {
    const { setPage: setPageAction, data } = this.props;
    const input = getCreateExpiredVraGracePeriodMutationInput(
      action,
      values
    );
    await mutation({
      variables: { ...input },
    })
      .then(({ data: mutationData }) => {
        const { success, msg } = mutationData[mutationResultName];
        if (success) {
          this.successSubmitAuctionException(action);
          this.onFormCloseHandler();
          if (action === 'Delete') {
            this.setState({
              isDeleteConfirmationOpen: false,
            });
          }
          setPageAction({});
          data.refetch();
        } else {
          this.onFailedSubmitAuctionException(action, msg);
        }
      })
      .catch(error => {
        this.onFailedSubmitAuctionException(
          action,
          Array.isArray(error) ? error : error.message
        );
      });
  };

  handleMessageDismiss = () => {
    this.setState({ flashMessage: null });
  };

  componentDidMount() {
    this.locationGetAllQuery();
  }

  render() {
    const {
      data: { autoCancellationExceptionList },
    } = this.props;
    const {
      selectedAuctionException,
      isDeleteConfirmationOpen,
      formSubmitting,
      isModalOpen,
      formError,
      flashMessage,
      isNegativeFlashMessage,
      locations,
    } = this.state;
    return (
      <div>
        {flashMessage && (
          <Message
            onDismiss={this.handleMessageDismiss}
            content={flashMessage}
            positive
            negative={isNegativeFlashMessage}
          />
        )}
        <Button inverted color='green' onClick={this.onClickNewAuction}>
          New Location Exception
        </Button>
        <br />
        <br />
        <AuctionExceptionList
          onSelectException={this.onSelectAuctionException}
          exceptions={autoCancellationExceptionList}
          exceptionType='AUCTION'
        />

        {isModalOpen && (
          <AuctionExceptionForm
            isModalOpen={isModalOpen}
            onCloseHandler={this.onFormCloseHandler}
            onSubmitHandler={this.onFormSubmitHandler}
            auctionException={selectedAuctionException}
            locations={locations}
            error={formError}
            submitting={formSubmitting}
          />
        )}

        <Confirm
          open={isDeleteConfirmationOpen}
          onCancel={this.handleDeleteCancel}
          onConfirm={this.handleDeleteConfirm}
        />
      </div>
    );
  }
}

AuctionExceptions.propTypes = {
  data: PropTypes.shape({
    autoCancellationExceptionList: PropTypes.arrayOf(PropTypes.object),
    refetch: PropTypes.func,
  }),
  createMutation: PropTypes.func,
  updateMutation: PropTypes.func,
  deleteMutation: PropTypes.func,
  setPage: PropTypes.func,
};

AuctionExceptions.defaultProps = {
  data: {
    autoCancellationExceptionList: [],
    refetch: () => {},
  },
  createMutation: () => {},
  updateMutation: () => {},
  deleteMutation: () => {},
  setPage: () => {},
};

const mapDispatchToProps = dispatch => ({
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { user },
  } = state;
  return {
    user,
  };
};
export default compose(
  withRouter,
  withPagination(
    autoCancellationExceptionListQuery,
    'autoCancellationExceptionList',
    { exceptionTypes: 'AUCTION' }
  ),
  graphql(createAutoCancellationExceptionMutation, {
    name: 'createMutation',
  }),
  graphql(updateAutoCancellationExceptionMutation, {
    name: 'updateMutation',
  }),
  graphql(deleteAutoCancellationExceptionMutation, {
    name: 'deleteMutation',
  }),
  connect(mapStateToProps, mapDispatchToProps)
)(AuctionExceptions);
